import { useEffect, useState, useCallback } from "react";
import { getDailyPuzzle, fenToGrid, rcToSq } from "../data/puzzles";
import { getDailyPuzzleStatus, completePuzzle } from "../lib/api";
import CrownCoin from "./CrownCoin";
import { DS } from "../theme";

const SQ = 52; // square size in px

const LIGHT = "#F0D9B5";
const DARK  = "#B58863";

const STREAK_MILESTONES = [3, 7, 14];

function streakBonusAt(streak) {
  if (streak >= 14) return 100;
  if (streak >= 7)  return 50;
  if (streak >= 3)  return 20;
  return 0;
}

function nextMilestone(streak) {
  return STREAK_MILESTONES.find(m => m > streak) || null;
}

// ── Board ─────────────────────────────────────────────────────────────────────

function PuzzleBoard({ grid, selected, onSquareClick, lastFrom, lastTo, solved, flipped }) {
  const rows = flipped ? [...Array(8)].map((_,i)=>7-i) : [...Array(8)].map((_,i)=>i);
  const cols = flipped ? [...Array(8)].map((_,i)=>7-i) : [...Array(8)].map((_,i)=>i);

  return (
    <div style={{
      display: "inline-grid",
      gridTemplateColumns: `repeat(8, ${SQ}px)`,
      gridTemplateRows:    `repeat(8, ${SQ}px)`,
      borderRadius: 6,
      overflow: "hidden",
      boxShadow: "0 8px 32px rgba(0,0,0,0.55)",
      border: `2px solid ${DS.borderMid}`,
    }}>
      {rows.map(r => cols.map(c => {
        const isLight    = (r + c) % 2 === 0;
        const piece      = grid[r][c];
        const isSelected = selected && selected[0] === r && selected[1] === c;
        const isLastMove = (lastFrom && lastFrom[0]===r && lastFrom[1]===c) ||
                           (lastTo   && lastTo[0]===r   && lastTo[1]===c);
        let bg = isLight ? LIGHT : DARK;
        if (isLastMove) bg = isLight ? "#cdd16f" : "#aaa23a";
        if (isSelected) bg = "#7fc97f";

        return (
          <div
            key={`${r}${c}`}
            onClick={() => onSquareClick(r, c)}
            style={{
              width: SQ, height: SQ,
              background: bg,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: solved ? "default" : "pointer",
              position: "relative",
              transition: "background 0.1s",
            }}
          >
            {/* Rank label on leftmost column */}
            {c === (flipped ? 7 : 0) && (
              <span style={{
                position: "absolute", top: 2, left: 3,
                fontSize: 9, fontWeight: 700,
                color: isLight ? DARK : LIGHT, opacity: 0.7,
                userSelect: "none", pointerEvents: "none",
              }}>{8 - r}</span>
            )}
            {/* File label on bottom row */}
            {r === (flipped ? 0 : 7) && (
              <span style={{
                position: "absolute", bottom: 2, right: 4,
                fontSize: 9, fontWeight: 700,
                color: isLight ? DARK : LIGHT, opacity: 0.7,
                userSelect: "none", pointerEvents: "none",
              }}>{String.fromCharCode(97 + c)}</span>
            )}
            {piece && (
              <span style={{
                fontSize: SQ * 0.68,
                lineHeight: 1,
                userSelect: "none",
                pointerEvents: "none",
                color: piece.isWhite ? "#FFFDF8" : "#1C1A2E",
                textShadow: piece.isWhite
                  ? "0 1px 0 #aaa, 0 0 6px rgba(0,0,0,0.4)"
                  : "0 1px 0 #555, 0 0 6px rgba(255,255,255,0.2)",
                filter: solved && !piece.isWhite ? "saturate(0.5)" : "none",
              }}>
                {piece.char}
              </span>
            )}
          </div>
        );
      }))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function DailyPuzzle({ user, crowns, onCrownsUpdate, onNavigate, onPlayBlanche }) {
  const puzzle = getDailyPuzzle();
  const grid0  = fenToGrid(puzzle.fen);

  const [grid,     setGrid]     = useState(grid0);
  const [selected, setSelected] = useState(null); // [r, c] | null
  const [lastFrom, setLastFrom] = useState(null);
  const [lastTo,   setLastTo]   = useState(null);
  const [phase, setPhase]       = useState("idle"); // idle | solved | failed | already_solved | loading
  const [streak,    setStreak]  = useState(0);
  const [earned,    setEarned]  = useState(0);
  const [streakBonus, setStreakBonus] = useState(0);
  const [hintsUsed, setHintsUsed]    = useState(false);
  const [showHint,  setShowHint]     = useState(false);
  const [shake,     setShake]        = useState(false);
  const [showCompletionMenu, setShowCompletionMenu] = useState(false);

  // Load today's completion status
  useEffect(() => {
    if (!user?.token) return;
    setPhase("loading");
    getDailyPuzzleStatus(user.token)
      .then(data => {
        if (data?.completed) {
          setStreak(data.streak || 0);
          setEarned(data.crownsEarned || 0);
          setPhase("already_solved");
          const _today = new Date().toISOString().slice(0, 10);
          localStorage.setItem('checkmate_daily_puzzle_completed_date', _today);
        } else {
          setPhase("idle");
        }
      })
      .catch(() => setPhase("idle"));
  }, [user?.token]); // eslint-disable-line

  const handleSquareClick = useCallback((r, c) => {
    if (phase !== "idle") return;
    const piece = grid[r][c];

    if (selected) {
      const [sr, sc] = selected;

      // Clicked same square — deselect
      if (sr === r && sc === c) { setSelected(null); return; }

      // Clicked another own piece — reselect
      if (piece && piece.isWhite === (puzzle.sideToMove === "white")) {
        setSelected([r, c]);
        return;
      }

      // Try move
      const from = rcToSq(sr, sc);
      const to   = rcToSq(r, c);
      const move = `${from}${to}`;

      // Check for promotion (pawn reaching last rank) — append 'q' by default
      const movingPiece = grid[sr][sc];
      const isPromotion = movingPiece?.char === '♙' && r === 0;
      const fullMove = isPromotion ? move + 'q' : move;

      if (fullMove === puzzle.solution) {
        // Correct!
        const newGrid = grid.map(row => row.map(sq => sq));
        newGrid[r][c]   = newGrid[sr][sc];
        newGrid[sr][sc] = null;
        setGrid(newGrid);
        setLastFrom([sr, sc]);
        setLastTo([r, c]);
        setSelected(null);
        setPhase("solved");
        const _today = new Date().toISOString().slice(0, 10);
        localStorage.setItem('checkmate_daily_puzzle_completed_date', _today);
        window.dispatchEvent(new Event('dailyPuzzleCompleted'));
        setTimeout(() => setShowCompletionMenu(true), 800);
        if (user?.token) {
          completePuzzle(user.token, puzzle.id, hintsUsed)
            .then(data => {
              if (!data?.alreadySolved) {
                setStreak(data.streak || 1);
                setEarned(data.earned || puzzle.crowns);
                setStreakBonus(data.streakBonus || 0);
                if (onCrownsUpdate && data.crowns !== undefined) onCrownsUpdate(data.crowns);
              } else {
                setStreak(data.streak || 1);
              }
            })
            .catch(() => {});
        }
      } else {
        // Wrong move
        setShake(true);
        setTimeout(() => setShake(false), 600);
        setSelected(null);
      }
    } else {
      // Select piece if it belongs to the side to move
      if (piece && piece.isWhite === (puzzle.sideToMove === "white")) {
        setSelected([r, c]);
      }
    }
  }, [grid, selected, phase, puzzle, hintsUsed, user, onCrownsUpdate]);

  const handleHint = () => {
    setHintsUsed(true);
    setShowHint(true);
  };

  const next = nextMilestone(streak);
  const isMilestone = STREAK_MILESTONES.includes(streak);

  return (
    <div style={{ maxWidth: 520, margin: "0 auto", padding: "0 0 40px" }}>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div className="cinzel" style={{ fontSize: 11, color: DS.textFaint, letterSpacing: "0.1em", marginBottom: 6 }}>
          DAILY PUZZLE — {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }).toUpperCase()}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div>
            <div className="cinzel" style={{ fontSize: 18, fontWeight: 700, color: DS.text }}>{puzzle.title}</div>
            <div style={{ fontSize: 12, color: DS.textMuted, marginTop: 2 }}>
              {puzzle.theme} · {puzzle.sideToMove === "white" ? "White" : "Black"} to move
            </div>
          </div>
          {streak > 0 && (
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              background: isMilestone ? `${DS.gold}18` : DS.elevated,
              border: `1px solid ${isMilestone ? DS.gold : DS.borderFaint}`,
              borderRadius: 8, padding: "6px 12px",
            }}>
              <span style={{ fontSize: 16 }}>🔥</span>
              <div>
                <div className="cinzel" style={{ fontSize: 13, fontWeight: 700, color: isMilestone ? DS.gold : DS.text }}>
                  {streak}-day streak
                </div>
                {next && (
                  <div style={{ fontSize: 10, color: DS.textFaint }}>{next - streak} more for +{streakBonusAt(next)} 👑</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Board */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
        <div style={{
          animation: shake ? "shake 0.5s ease" : "none",
        }}>
          <style>{`
            @keyframes shake {
              0%,100%{transform:translateX(0)}
              20%{transform:translateX(-6px)}
              40%{transform:translateX(6px)}
              60%{transform:translateX(-4px)}
              80%{transform:translateX(4px)}
            }
          `}</style>
          <PuzzleBoard
            grid={grid}
            selected={selected}
            onSquareClick={handleSquareClick}
            lastFrom={lastFrom}
            lastTo={lastTo}
            solved={phase === "solved" || phase === "already_solved"}
            flipped={puzzle.sideToMove === "black"}
          />
        </div>
      </div>

      {/* Status panels */}
      {phase === "loading" && (
        <div style={{ textAlign: "center", color: DS.textFaint, fontSize: 13 }}>Loading…</div>
      )}

      {phase === "idle" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {showHint ? (
            <div style={{
              background: `${DS.gold}10`, border: `1px solid ${DS.gold}30`,
              borderRadius: 8, padding: "12px 16px", fontSize: 13, color: DS.textMuted, lineHeight: 1.6,
            }}>
              <span style={{ color: DS.gold, fontWeight: 600 }}>Hint: </span>{puzzle.hint}
            </div>
          ) : (
            <button className="btn btn-ghost" onClick={handleHint} style={{ fontSize: 12 }}>
              Show Hint (reduces crown reward by 50%)
            </button>
          )}
          <div style={{ fontSize: 11, color: DS.textFaint, textAlign: "center" }}>
            Click a piece to select, then click its destination
          </div>
        </div>
      )}

      {phase === "solved" && (
        <div style={{
          background: `${DS.success}12`, border: `1px solid ${DS.success}35`,
          borderRadius: 10, padding: "20px 24px", textAlign: "center",
        }}>
          <div className="cinzel" style={{ fontSize: 15, fontWeight: 700, color: DS.success, marginBottom: 8 }}>
            Correct!
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 24 }}>+</span>
            <CrownCoin size={24} shimmer />
            <span className="cinzel" style={{ fontSize: 22, fontWeight: 700, color: "#D4AF37" }}>
              {earned}
            </span>
          </div>
          {streakBonus > 0 && (
            <div style={{ fontSize: 12, color: DS.gold, marginBottom: 8 }}>
              🔥 {streak}-day streak bonus: +{streakBonus} 👑 included!
            </div>
          )}
          {streak > 0 && (
            <div style={{ fontSize: 12, color: DS.textMuted }}>
              {streak}-day streak{next ? ` · ${next - streak} more for +${streakBonusAt(next)} 👑 bonus` : " · Max streak milestone reached!"}
            </div>
          )}
        </div>
      )}

      {phase === "already_solved" && (
        <div style={{
          background: `${DS.accent}10`, border: `1px solid ${DS.accent}25`,
          borderRadius: 10, padding: "20px 24px", textAlign: "center",
        }}>
          <div className="cinzel" style={{ fontSize: 14, fontWeight: 700, color: DS.accent, marginBottom: 6 }}>
            Already solved today
          </div>
          <div style={{ fontSize: 12, color: DS.textMuted, marginBottom: earned > 0 ? 10 : 0 }}>
            Come back tomorrow for a new puzzle.
          </div>
          {earned > 0 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              <span style={{ fontSize: 12, color: DS.textFaint }}>Today you earned</span>
              <CrownCoin size={14} />
              <span className="cinzel" style={{ fontSize: 13, color: "#D4AF37", fontWeight: 700 }}>{earned}</span>
            </div>
          )}
          {streak > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: DS.gold }}>
              🔥 {streak}-day streak
            </div>
          )}
        </div>
      )}

      {!user && (
        <div style={{
          background: DS.elevated, border: `1px solid ${DS.borderFaint}`,
          borderRadius: 10, padding: "16px 20px", textAlign: "center", marginTop: 12,
        }}>
          <div style={{ fontSize: 13, color: DS.textMuted, marginBottom: 8 }}>
            Sign in to save your streak and earn crowns.
          </div>
        </div>
      )}

      {/* Completion menu overlay */}
      {showCompletionMenu && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 400,
          background: "rgba(13,12,24,0.82)", backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          animation: "fadeUp 0.2s ease both",
        }}>
          <div style={{
            background: DS.surface, border: `1px solid ${DS.borderFaint}`,
            borderRadius: 16, padding: "36px 32px", width: 300, textAlign: "center",
            boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
          }}>
            <div style={{
              fontSize: 40, color: DS.gold, marginBottom: 14,
              filter: `drop-shadow(0 0 16px ${DS.goldGlow})`,
            }}>✦</div>
            <div className="cinzel-deco" style={{
              fontSize: 18, fontWeight: 700, color: DS.text,
              letterSpacing: "0.1em", marginBottom: 6,
            }}>
              Puzzle Complete
            </div>
            <div className="cinzel" style={{
              fontSize: 11, color: DS.textMuted, letterSpacing: "0.2em",
              textTransform: "uppercase", marginBottom: 28,
            }}>
              Well played.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                className="btn btn-ghost cinzel"
                style={{ width: "100%", fontSize: 12, letterSpacing: "0.05em" }}
                onClick={() => { setShowCompletionMenu(false); onNavigate?.("shop"); }}
              >
                ✦ Check New Shop Items
              </button>
              <button
                className="btn btn-primary cinzel"
                style={{ width: "100%", fontSize: 12, letterSpacing: "0.05em" }}
                onClick={() => { setShowCompletionMenu(false); onPlayBlanche?.(); }}
              >
                ♛ Train with Blanche
              </button>
              <button
                className="btn btn-ghost btn-sm cinzel"
                style={{ width: "100%", marginTop: 4 }}
                onClick={() => { setShowCompletionMenu(false); onNavigate?.("menu"); }}
              >
                ← Main Menu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
